import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

interface PopoverCtx {
  open: boolean;
  setOpen: (next: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentId: string;
}

const Ctx = React.createContext<PopoverCtx | null>(null);

function usePopoverCtx() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("Popover components must be used within <Popover>");
  return ctx;
}

interface PopoverProps {
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  children: React.ReactNode;
}

const Popover: React.FC<PopoverProps> = ({ open, onOpenChange, children }) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const isControlled = typeof open === "boolean";
  const currentOpen = isControlled ? (open as boolean) : internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  const contentId = React.useId();
  const value = React.useMemo(
    () => ({ open: currentOpen, setOpen, triggerRef, contentId }),
    [currentOpen, setOpen, contentId],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

interface PopoverTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  ({ asChild = false, children, onClick, ...props }, forwardedRef) => {
    const { open, setOpen, triggerRef, contentId } = usePopoverCtx();
    const setRefs = React.useCallback(
      (node: HTMLElement | null) => {
        triggerRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node as HTMLButtonElement);
        else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLButtonElement | null>).current =
            node as HTMLButtonElement;
        }
      },
      [forwardedRef, triggerRef],
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      onClick?.(event as React.MouseEvent<HTMLButtonElement>);
      if (event.defaultPrevented) return;
      setOpen(!open);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<any>,
        {
          ref: setRefs,
          "aria-expanded": open,
          "aria-controls": contentId,
          onClick: handleClick,
        },
      );
    }
    return (
      <button
        type="button"
        ref={setRefs}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  },
);
PopoverTrigger.displayName = "PopoverTrigger";

interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  sideOffset?: number;
  collisionPadding?: number;
  avoidCollisions?: boolean;
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      className,
      align = "center",
      side = "bottom",
      sideOffset = 8,
      collisionPadding = 8,
      avoidCollisions = true,
      style,
      children,
      ...props
    },
    forwardedRef,
  ) => {
    const { open, setOpen, triggerRef, contentId } = usePopoverCtx();
    const localRef = React.useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = React.useState<{
      top: number;
      left: number;
      maxHeight: number;
    } | null>(null);

    const position = React.useCallback(() => {
      const trigger = triggerRef.current;
      const content = localRef.current;
      if (!trigger || !content) return;
      const triggerRect = trigger.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      const spaceBelow = vh - triggerRect.bottom - collisionPadding;
      const spaceAbove = triggerRect.top - collisionPadding;

      let effectiveSide = side;
      if (avoidCollisions) {
        const needed = contentRect.height + sideOffset;
        if (side === "bottom" && spaceBelow < needed && spaceAbove > spaceBelow) {
          effectiveSide = "top";
        } else if (side === "top" && spaceAbove < needed && spaceBelow > spaceAbove) {
          effectiveSide = "bottom";
        }
      }

      const available = Math.max(
        120,
        effectiveSide === "bottom"
          ? vh - triggerRect.bottom - sideOffset - collisionPadding
          : triggerRect.top - sideOffset - collisionPadding,
      );

      const top =
        effectiveSide === "bottom"
          ? triggerRect.bottom + sideOffset
          : triggerRect.top - Math.min(contentRect.height, available) - sideOffset;

      let left = triggerRect.left;
      if (align === "center") {
        left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
      } else if (align === "end") {
        left = triggerRect.right - contentRect.width;
      }
      const maxLeft = Math.max(
        collisionPadding,
        vw - contentRect.width - collisionPadding,
      );
      left = Math.min(Math.max(collisionPadding, left), maxLeft);

      // Content is `position: fixed`, so use the viewport-relative rect values
      // directly. Adding window.scrollX/scrollY (correct only for absolute
      // positioning) pushed the popover off by the page scroll amount, hiding
      // it below the viewport on any scrolled page.
      setCoords({
        top,
        left,
        maxHeight: available,
      });
    }, [align, side, sideOffset, collisionPadding, avoidCollisions, triggerRef]);

    React.useLayoutEffect(() => {
      if (!open) return;
      position();
    }, [open, position]);

    React.useEffect(() => {
      if (!open) return;
      const handler = () => position();
      window.addEventListener("scroll", handler, true);
      window.addEventListener("resize", handler);
      return () => {
        window.removeEventListener("scroll", handler, true);
        window.removeEventListener("resize", handler);
      };
    }, [open, position]);

    React.useEffect(() => {
      if (!open) return;
      const onDown = (event: MouseEvent) => {
        const t = event.target as Node;
        if (
          localRef.current?.contains(t) ||
          triggerRef.current?.contains(t)
        ) {
          return;
        }
        setOpen(false);
      };
      const onKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("mousedown", onDown);
        document.removeEventListener("keydown", onKey);
      };
    }, [open, setOpen, triggerRef]);

    if (!open || typeof document === "undefined") return null;

    const setRefs = (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current =
          node;
      }
    };

    return createPortal(
      <div
        ref={setRefs}
        id={contentId}
        role="dialog"
        data-state={open ? "open" : "closed"}
        className={cn(
          "fixed z-50 w-72 max-w-[calc(100vw-16px)] overflow-y-auto overscroll-contain rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none",
          className,
        )}
        style={{
          ...style,
          top: coords?.top ?? -9999,
          left: coords?.left ?? -9999,
          maxHeight: coords?.maxHeight,
          visibility: coords ? "visible" : "hidden",
        }}
        {...props}
      >
        {children}
      </div>,
      document.body,
    );
  },
);
PopoverContent.displayName = "PopoverContent";

export { Popover, PopoverTrigger, PopoverContent };
